import React, { useEffect, useState } from 'react';
import { subscribeOnlineStatus, isOnline } from '../../utils/serviceWorker';
import { isLikelyOffline } from '../../utils/networkStatus';

/**
 * OfflineBanner — a small, non-blocking strip that surfaces network state so
 * users understand why their actions might be queued or read-only.
 *
 * Renders nothing while online. Slides down from the top while offline.
 *
 * Detection note: we treat the user as offline whenever EITHER
 *   - navigator.onLine === false (OS-level signal), OR
 *   - a recent axios request died with a network error (the sticky offline
 *     flag set by our axios interceptor). This matters on mobile where the
 *     OS often reports online even when the network is unreachable.
 */
const OfflineBanner = () => {
  const [online, setOnline] = useState(!isLikelyOffline());

  useEffect(() => {
    // Re-evaluate every second so the sticky-offline flag from failed
    // requests can flip the banner without waiting for an OS event.
    const update = () => setOnline(!isLikelyOffline());
    const unsubscribe = subscribeOnlineStatus(() => update());
    const interval = window.setInterval(update, 1500);
    return () => {
      unsubscribe();
      window.clearInterval(interval);
    };
  }, []);

  if (online) return null;

  return (
    <div
      data-testid="offline-banner"
      className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-white text-sm px-4 py-2 flex items-center justify-center gap-2 shadow-md"
      role="status"
      aria-live="polite"
    >
      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636l-12.728 12.728M5.636 5.636l12.728 12.728" />
      </svg>
      <span>You're offline — viewing cached data. Edits will sync when you're back online.</span>
    </div>
  );
};

export default OfflineBanner;
